import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection, LegalDisclaimer, LegalLastUpdated } from '../LegalLayout';
import ProductsSiteLayout from '../../LandingPage/products/ProductsSiteLayout';
import { APP_NAME, APP_DESCRIPTION } from '../../config/branding';
import { MIN_AGE_YEARS } from '../legalConstants';

export default function DocumentationPage() {
  return (
    <ProductsSiteLayout activeNav="docs" mainClassName="products-page-main--legal-doc">
      <LegalLayout
        embedded
        title="Documentation"
        subtitle={`Your guide to ${APP_NAME} — what it is, what to expect, and how to get the most from it.`}
      >
      <LegalLastUpdated />

      <LegalSection title="What is Bahlus?">
        <p>
          {APP_NAME} is a community forum built for open discussion, shared stories, and topic-based clubs.
          {APP_DESCRIPTION ? ` ${APP_DESCRIPTION}` : ''} It is a place to ask honest questions, exchange
          perspectives, and build belonging in spaces designed for dialogue — not noise.
        </p>
        <p>
          Whether you are exploring as a guest or joining as a member, {APP_NAME} connects curiosity with
          community: discussions you can follow, clubs you can join, and stories that carry knowledge forward.
        </p>
      </LegalSection>

      <LegalSection title="What you can expect">
        <div className="legal-card-grid">
          <div className="legal-card">
            <h3>Open discussions</h3>
            <p>Start threads, reply with context, vote on helpful contributions, and discover conversations through hashtags and search.</p>
          </div>
          <div className="legal-card">
            <h3>Clubs & communities</h3>
            <p>Join or create focused spaces around topics you care about — from hobbies to professional interests.</p>
          </div>
          <div className="legal-card">
            <h3>Stories</h3>
            <p>Share longer-form knowledge, experiences, and narratives that deserve more room than a quick post.</p>
          </div>
          <div className="legal-card">
            <h3>Social connections</h3>
            <p>Follow people, receive notifications, send messages, and build a profile that reflects how you want to be seen.</p>
          </div>
        </div>
        <p>
          {APP_NAME} is moderated for safety and civility. We provide tools to report content and expect members to
          follow our <Link to="/legal/code-of-conduct" className="legal-inline-link">Code of Conduct</Link> and{' '}
          <Link to="/legal/acceptable-use" className="legal-inline-link">Acceptable Use</Link> guidelines.
        </p>
      </LegalSection>

      <LegalSection title="How it works — a quick walkthrough">
        <ol>
          <li>
            <strong>Create an account</strong> (you must be at least {MIN_AGE_YEARS} years old) or browse publicly where
            guest viewing is enabled.
          </li>
          <li>
            <strong>Explore discussions</strong> on the main feed — filter by clubs, search topics, and read threads before
            you contribute.
          </li>
          <li>
            <strong>Join clubs</strong> that match your interests. Clubs keep conversations focused and help you find
            people who share your curiosity.
          </li>
          <li>
            <strong>Post and comment</strong> using your active profile identity (see Personal vs Social below).
          </li>
          <li>
            <strong>Manage your profile</strong> — update your handle, picture, bio, and privacy preferences from profile
            settings after sign-in.
          </li>
        </ol>
      </LegalSection>

      <LegalSection title="Personal profile vs Social profile — why it matters">
        <div className="legal-callout">
          <p className="legal-callout-title">Two identities, one account</p>
          <p style={{ margin: 0 }}>
            {APP_NAME} lets you maintain separate <strong>Personal</strong> and <strong>Social</strong> profiles.
            They do not overlap — each has its own handle, display name, picture, followers, posts, and comments.
          </p>
        </div>

        <p>
          <strong>Personal profile</strong> is for people you know — close acquaintances, colleagues, and connections
          you are already aware of. Use it when you want your activity to stay within a familiar circle. Posts,
          comments, followers, and visibility remain separate from your social identity.
        </p>
        <p>
          <strong>Social profile</strong> is optional. It is designed for the wider community — open questions,
          public perspectives, and dialogue with anyone on the platform. Your social handle, avatar, bio, and activity
          are independent from your personal profile.
        </p>
        <p>
          <strong>Why switch?</strong> Context matters. You might share professional insights under your social profile
          while keeping personal updates for friends. You might experiment with public writing without mixing it with
          private connections. Switching modes updates your feed, followers context, and how others see you — so always
          check which profile is active before you post.
        </p>
        <p>
          Switch profiles from the header dropdown or from Manage Profile → Identity. When you change modes, cached
          feeds refresh so you see content relevant to the active identity.
        </p>
      </LegalSection>

      <LegalSection title="Guest browsing">
        <p>
          Where enabled, guests may browse selected public discussions without signing in. Guest access is read-focused;
          creating posts, commenting, messaging, and profile features require an account. Availability may vary by
          community configuration.
        </p>
      </LegalSection>

      <LegalSection title="Account requirements">
        <ul>
          <li>Minimum age: {MIN_AGE_YEARS} years</li>
          <li>Valid email address and secure password (minimum 8 characters)</li>
          <li>Agreement to our <Link to="/terms" className="legal-inline-link">Terms & Conditions</Link> and acknowledgment of our{' '}
            <Link to="/privacy" className="legal-inline-link">Privacy Policy</Link></li>
          <li>Human verification (Cloudflare Turnstile) on sign-in and sign-up where configured</li>
        </ul>
      </LegalSection>

      <LegalSection title="Safety & support">
        <p>
          If you see content that violates our policies, use in-app reporting where available or visit our{' '}
          <Link to="/legal/safety" className="legal-inline-link">Safety & Reporting</Link> page. <br/> <br/>
          For general help, {' '}
          {/* email <a href={`mailto:${SUPPORT_EMAIL}`} className="legal-inline-link">{SUPPORT_EMAIL}</a> or use the{' '} */}
          <Link to="/contact" className="legal-inline-link">Contact</Link> form.
        </p>
      </LegalSection>

      <LegalSection title="Related policies">
        <ul>
          <li><Link to="/privacy" className="legal-inline-link">Privacy Policy</Link></li>
          <li><Link to="/terms" className="legal-inline-link">Terms & Conditions</Link></li>
          <li><Link to="/legal/code-of-conduct" className="legal-inline-link">Code of Conduct</Link></li>
          <li><Link to="/legal/fair-use" className="legal-inline-link">Fair Use Policy</Link></li>
          <li><Link to="/legal/regional" className="legal-inline-link">Regional Compliance</Link></li>
          <li><Link to="/legal/open-source" className="legal-inline-link">Open Source Licenses</Link></li>
        </ul>
      </LegalSection>

      <LegalDisclaimer />
      </LegalLayout>
    </ProductsSiteLayout>
  );
}
